/**
 * Email Template Page
 * AIDLC Administrative Frontend
 *
 * Manage email templates: list, create, edit, delete, preview.
 * Each template has a subject/body with {{variable}} placeholders.
 * Live preview with sample data via API call.
 *
 * Personas: ops, admin
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  previewTemplate,
  getTemplateVariables,
} from '../../services/emailTemplateService';
import { getAllAgents } from '../../services/agentService';
import type { EmailTemplate, EmailTemplateCategory, TemplateVariable } from '../../types/email';
import type { Agent } from '../../types/agent';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { Badge } from '../shared/Badge';

// ── Constants ──────────────────────────────────────────────────────

const CATEGORIES: EmailTemplateCategory[] = [
  'status',
  'notification',
  'outreach',
  'internal',
  'escalation',
];

const CATEGORY_LABELS: Record<EmailTemplateCategory, string> = {
  status: 'Status',
  notification: 'Notification',
  outreach: 'Outreach',
  internal: 'Internal',
  escalation: 'Escalation',
};

const CATEGORY_BADGE_VARIANT: Record<EmailTemplateCategory, 'info' | 'success' | 'warning' | 'danger'> = {
  status: 'info',
  notification: 'warning',
  outreach: 'success',
  internal: 'info',
  escalation: 'danger',
};

type ModalMode = 'create' | 'edit';

interface TemplateFormData {
  name: string;
  description: string;
  category: EmailTemplateCategory;
  subjectTemplate: string;
  bodyTemplate: string;
  defaultAgentId: string;
}

const EMPTY_FORM: TemplateFormData = {
  name: '',
  description: '',
  category: 'status',
  subjectTemplate: '',
  bodyTemplate: '',
  defaultAgentId: '',
};

// ── Helpers ────────────────────────────────────────────────────────

function extractVariables(text: string): string[] {
  const matches = text.match(/\{\{(\w+(?:\.\w+)*)\}\}/g);
  if (!matches) return [];
  return [...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, '')))];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Main Page ──────────────────────────────────────────────────────

export function EmailTemplatePage() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [form, setForm] = useState<TemplateFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{ subject: string; body: string; variables: Record<string, string> } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Variable reference panel
  const [variablePanelOpen, setVariablePanelOpen] = useState(false);

  // ── Data Loading ──────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [templatesData, variablesData, agentsData] = await Promise.all([
        getTemplates(),
        getTemplateVariables(),
        getAllAgents(),
      ]);
      setTemplates(templatesData);
      setVariables(variablesData);
      setAgents(agentsData);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load templates';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Modal Handlers ────────────────────────────────────────────

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setEditingTemplate(null);
    setModalMode('create');
    setSaveError(null);
    setModalOpen(true);
  };

  const openEditModal = (template: EmailTemplate) => {
    setForm({
      name: template.name,
      description: template.description,
      category: template.category,
      subjectTemplate: template.subjectTemplate,
      bodyTemplate: template.bodyTemplate,
      defaultAgentId: template.defaultAgentId || '',
    });
    setEditingTemplate(template);
    setModalMode('edit');
    setSaveError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTemplate(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subjectTemplate.trim() || !form.bodyTemplate.trim()) {
      setSaveError('Name, subject template, and body template are required.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        subjectTemplate: form.subjectTemplate.trim(),
        bodyTemplate: form.bodyTemplate.trim(),
        defaultAgentId: form.defaultAgentId || undefined,
      };

      if (modalMode === 'create') {
        const created = await createTemplate(payload);
        setTemplates((prev) => [...prev, created]);
      } else if (editingTemplate) {
        const updated = await updateTemplate(editingTemplate.id, payload);
        setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      }

      closeModal();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save template';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Preview Handlers ──────────────────────────────────────────

  const openPreview = async (template: EmailTemplate) => {
    setPreviewTemplateId(template.id);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(true);
    setPreviewOpen(true);

    try {
      const data = await previewTemplate(template.id);
      setPreviewData(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate preview';
      setPreviewError(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewTemplateId(null);
    setPreviewData(null);
    setPreviewError(null);
  };

  // ── Delete Handlers ───────────────────────────────────────────

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      setDeleteConfirmId(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete template';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  };

  // ── Form field updater ────────────────────────────────────────

  const updateField = <K extends keyof TemplateFormData>(field: K, value: TemplateFormData[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ── Derived values ────────────────────────────────────────────

  const formVariables = extractVariables(form.subjectTemplate + ' ' + form.bodyTemplate);
  const previewTemplateName = previewTemplateId
    ? templates.find((t) => t.id === previewTemplateId)?.name ?? 'Template'
    : 'Template';

  // ── Render ────────────────────────────────────────────────────

  if (loading) {
    return <LoadingSpinner message="Loading email templates..." />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage reusable email templates with variable placeholders
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setVariablePanelOpen((v) => !v)}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
            </svg>
            Variables
          </button>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 text-sm bg-ldaja-blue text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Create Template
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Variable Reference Panel */}
      {variablePanelOpen && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-ldaja-black">Available System Variables</h3>
            <button
              onClick={() => setVariablePanelOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {variables.length === 0 ? (
            <p className="text-sm text-gray-500">No system variables available.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {variables.map((v) => (
                <div key={v.name} className="bg-white rounded-md border border-gray-200 px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-xs font-mono bg-blue-50 text-ldaja-blue px-1.5 py-0.5 rounded">
                      {'{{' + v.name + '}}'}
                    </code>
                  </div>
                  <p className="text-xs text-gray-600">{v.description}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Source: {v.source}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Template Grid */}
      {templates.length === 0 ? (
        <div className="text-center py-16">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
          <h3 className="text-lg font-medium text-gray-700 mb-1">No email templates yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Create your first template to start sending automated emails.
          </p>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 text-sm bg-ldaja-blue text-white hover:bg-blue-700 rounded-lg transition-colors"
          >
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => openEditModal(template)}
              onPreview={() => openPreview(template)}
              onDelete={() => setDeleteConfirmId(template.id)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <TemplateModal
          mode={modalMode}
          form={form}
          formVariables={formVariables}
          agents={agents}
          variables={variables}
          saving={saving}
          saveError={saveError}
          onUpdateField={updateField}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}

      {/* Preview Modal */}
      {previewOpen && (
        <PreviewModal
          templateName={previewTemplateName}
          data={previewData}
          loading={previewLoading}
          error={previewError}
          onClose={closePreview}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <DeleteConfirmDialog
          templateName={templates.find((t) => t.id === deleteConfirmId)?.name ?? 'this template'}
          deleting={deleting}
          onConfirm={() => handleDelete(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      )}
    </div>
  );
}

// ── Template Card ──────────────────────────────────────────────────

function TemplateCard({
  template,
  onEdit,
  onPreview,
  onDelete,
}: {
  template: EmailTemplate;
  onEdit: () => void;
  onPreview: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-semibold text-ldaja-black truncate pr-2">{template.name}</h3>
        <Badge variant={CATEGORY_BADGE_VARIANT[template.category]}>
          {CATEGORY_LABELS[template.category]}
        </Badge>
      </div>

      {/* Description preview */}
      <p className="text-xs text-gray-500 mb-3 line-clamp-2 min-h-[2rem]">
        {template.description || 'No description'}
      </p>

      {/* Subject line preview */}
      <div className="bg-gray-50 rounded-md px-3 py-2 mb-3">
        <p className="text-[11px] text-gray-400 mb-0.5">Subject</p>
        <p className="text-xs text-gray-700 font-mono truncate">{template.subjectTemplate}</p>
      </div>

      {/* Meta info */}
      <div className="flex items-center justify-between text-[11px] text-gray-400 mb-3">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
            </svg>
            {template.variables.length} variable{template.variables.length !== 1 ? 's' : ''}
          </span>
          <span>by {template.createdBy}</span>
        </div>
        <span>{formatDate(template.createdAt)}</span>
      </div>

      {/* Variable chips */}
      {template.variables.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {template.variables.slice(0, 4).map((v) => (
            <span
              key={v}
              className="text-[10px] font-mono bg-blue-50 text-ldaja-blue px-1.5 py-0.5 rounded"
            >
              {v}
            </span>
          ))}
          {template.variables.length > 4 && (
            <span className="text-[10px] text-gray-400">
              +{template.variables.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={onEdit}
          className="flex-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
        >
          Edit
        </button>
        <button
          onClick={onPreview}
          className="flex-1 px-3 py-1.5 text-xs bg-ldaja-blue text-white hover:bg-blue-700 rounded-md transition-colors"
        >
          Preview
        </button>
        <button
          onClick={onDelete}
          className="px-2 py-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
          title="Delete template"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Create/Edit Modal ──────────────────────────────────────────────

function TemplateModal({
  mode,
  form,
  formVariables,
  agents,
  variables,
  saving,
  saveError,
  onUpdateField,
  onSave,
  onClose,
}: {
  mode: ModalMode;
  form: TemplateFormData;
  formVariables: string[];
  agents: Agent[];
  variables: TemplateVariable[];
  saving: boolean;
  saveError: string | null;
  onUpdateField: <K extends keyof TemplateFormData>(field: K, value: TemplateFormData[K]) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal Content */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {mode === 'create' ? 'Create Template' : 'Edit Template'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-ldaja-black mb-1.5">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => onUpdateField('name', e.target.value)}
              placeholder="e.g., Sprint Completion Report"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-ldaja-black mb-1.5">
              Description
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => onUpdateField('description', e.target.value)}
              placeholder="Brief description of when this template is used"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue"
            />
          </div>

          {/* Category + Agent Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-ldaja-black mb-1.5">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={form.category}
                onChange={(e) => onUpdateField('category', e.target.value as EmailTemplateCategory)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue bg-white"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABELS[cat]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-ldaja-black mb-1.5">
                Default Agent <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                value={form.defaultAgentId}
                onChange={(e) => onUpdateField('defaultAgentId', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue bg-white"
              >
                <option value="">No default agent</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} - {agent.role}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Subject Template */}
          <div>
            <label className="block text-sm font-semibold text-ldaja-black mb-1.5">
              Subject Template <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.subjectTemplate}
              onChange={(e) => onUpdateField('subjectTemplate', e.target.value)}
              placeholder="e.g., [AIDLC] {{project.name}} - Sprint {{sprint.number}} Complete"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Use {'{{variableName}}'} syntax for dynamic placeholders
            </p>
          </div>

          {/* Body Template */}
          <div>
            <label className="block text-sm font-semibold text-ldaja-black mb-1.5">
              Body Template <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.bodyTemplate}
              onChange={(e) => onUpdateField('bodyTemplate', e.target.value)}
              rows={10}
              placeholder={`Hi {{recipient.name}},\n\nThis is to inform you that...\n\n{{agent.valediction}}\n{{agent.name}}\n{{agent.title}}`}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-ldaja-blue focus:border-ldaja-blue resize-none"
            />
          </div>

          {/* Detected Variables */}
          {formVariables.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-ldaja-black mb-2">
                Detected Variables ({formVariables.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {formVariables.map((v) => {
                  const knownVar = variables.find((sv) => sv.name === v);
                  return (
                    <span
                      key={v}
                      className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-md ${
                        knownVar
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                      }`}
                      title={knownVar ? `${knownVar.description} (source: ${knownVar.source})` : 'Custom variable - not in system variable list'}
                    >
                      {knownVar ? (
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                        </svg>
                      )}
                      {v}
                    </span>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">
                Green = recognized system variable. Yellow = custom variable (must be supplied at send time).
              </p>
            </div>
          )}

          {/* Quick Variable Reference (collapsed in modal) */}
          {variables.length > 0 && (
            <details className="group">
              <summary className="text-sm font-semibold text-ldaja-black cursor-pointer flex items-center gap-1.5 select-none">
                <svg className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
                Quick Variable Reference
              </summary>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {variables.map((v) => (
                  <button
                    key={v.name}
                    type="button"
                    className="text-left bg-gray-50 rounded-md px-2.5 py-1.5 hover:bg-gray-100 transition-colors"
                    title={`Click to copy: {{${v.name}}}`}
                    onClick={() => navigator.clipboard.writeText(`{{${v.name}}}`)}
                  >
                    <code className="text-[11px] font-mono text-ldaja-blue">
                      {'{{' + v.name + '}}'}
                    </code>
                    <p className="text-[10px] text-gray-500 mt-0.5">{v.description}</p>
                  </button>
                ))}
              </div>
            </details>
          )}

          {/* Save Error */}
          {saveError && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-5 py-2 text-sm bg-ldaja-blue text-white hover:bg-blue-700 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </>
            ) : mode === 'create' ? (
              'Create Template'
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Preview Modal ──────────────────────────────────────────────────

function PreviewModal({
  templateName,
  data,
  loading,
  error,
  onClose,
}: {
  templateName: string;
  data: { subject: string; body: string; variables: Record<string, string> } | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal Content */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Template Preview</h2>
              <p className="text-xs text-gray-500 mt-0.5">{templateName} - rendered with sample data</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {loading && <LoadingSpinner message="Generating preview..." size="sm" />}

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {data && (
            <div className="space-y-5">
              {/* Rendered Subject */}
              <div>
                <p className="text-sm font-semibold text-ldaja-black mb-1.5">Subject</p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
                  <p className="text-sm text-gray-900">{data.subject}</p>
                </div>
              </div>

              {/* Rendered Body */}
              <div>
                <p className="text-sm font-semibold text-ldaja-black mb-1.5">Body</p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                    {data.body}
                  </pre>
                </div>
              </div>

              {/* Variables Used */}
              {Object.keys(data.variables).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-ldaja-black mb-1.5">
                    Sample Variables Used
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Variable</th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Sample Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {Object.entries(data.variables).map(([key, value]) => (
                          <tr key={key}>
                            <td className="px-4 py-1.5">
                              <code className="text-xs font-mono text-ldaja-blue">
                                {'{{' + key + '}}'}
                              </code>
                            </td>
                            <td className="px-4 py-1.5 text-xs text-gray-700">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Confirmation Dialog ─────────────────────────────────────

function DeleteConfirmDialog({
  templateName,
  deleting,
  onConfirm,
  onCancel,
}: {
  templateName: string;
  deleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Delete Template</h3>
            <p className="text-sm text-gray-600 mt-1">
              Are you sure you want to delete <strong>{templateName}</strong>? This action cannot be
              undone. Any automation rules referencing this template will stop working.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {deleting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Deleting...
              </>
            ) : (
              'Delete Template'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
